// src/CreatorUser/CreatorPages/Dashboard/Bio/EnhancedPreviewHelper.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  Button,
  Paper,
  Grid,
  Fade,
  Slide,
  Zoom,
  Stack
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Launch,
  Star,
  Business,
  Public,
  QrCode,           // available in @mui/icons-material
  Link as LinkIcon,
  Image as ImageIcon,
  PlayArrow,
  Phone,
  Email,
  LocationOn
} from '@mui/icons-material';

/* -------------------------------- Animations ------------------------------- */
const fadeInAnimation = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUpAnimation = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseAnimation = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

/* ------------------------------ Styled helpers ----------------------------- */

// filter custom props so they don't end up as DOM attributes
const shouldForward = (blocked = []) => (prop) => !blocked.includes(prop);

export const BioPageContainer = styled(
  Box,
  { shouldForwardProp: shouldForward(['customtheme']) }
)(({ theme, customtheme }) => ({
  backgroundColor: customtheme?.backgroundColor || theme.palette.background.default,
  color: customtheme?.foregroundColor || theme.palette.text.primary,
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: theme.typography.fontFamily,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  padding: theme.spacing(4, 3, 3, 3),
  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
  backdropFilter: 'blur(10px)',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3, 4, 3),
  position: 'relative',
}));

const AnimatedItem = styled(
  Card,
  { shouldForwardProp: shouldForward(['animationType', 'animationsEnabled', 'size', 'delay']) }
)(({ theme, animationType, animationsEnabled, size, delay = 0 }) => {
  const getSize = () => {
    switch (size) {
      case 'SMALL': return { minHeight: 60, fontSize: '0.875rem' };
      case 'LARGE': return { minHeight: 120, fontSize: '1.125rem' };
      default: return { minHeight: 80, fontSize: '1rem' };
    }
  };

  const getAnimation = () => {
    if (!animationsEnabled) return 'none';
    switch (animationType) {
      case 'SLIDE_UP':
        return `${slideUpAnimation} 0.8s ease-out ${Number(delay)}s both`;
      case 'FADE_IN':
      default:
        return `${fadeInAnimation} 0.6s ease-out ${Number(delay)}s both`;
    }
  };

  const sizeProps = getSize();

  return {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    animation: getAnimation(),
    cursor: 'pointer',
    overflow: 'hidden',
    minHeight: sizeProps.minHeight,
    fontSize: sizeProps.fontSize,
    '&:hover': {
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
      animation: animationsEnabled ? `${pulseAnimation} 1s ease-in-out` : 'none'
    },
    '&:active': {
      transform: 'scale(0.98)'
    }
  };
});

const MediaContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 'auto',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ContactInfo = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  backgroundColor: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
}));

const QRCodePlaceholder = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  backgroundColor: theme.palette.common.black,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  fontSize: '0.75rem',
  fontWeight: 'bold',
  margin: theme.spacing(2, 'auto', 0),
}));

/* --------------------------------- Utils ---------------------------------- */
const isVideoUrl = (url = '') => /\.(mp4|webm|ogg|mov|avi)$/i.test(url);
const isImageUrl = (url = '') => /\.(gif|jpe?g|tiff?|png|webp|bmp|svg)$/i.test(url);

const getMimeTypeFromExt = (url = '') => {
  const ext = (url.split('.').pop() || '').toLowerCase();
  switch (ext) {
    case 'mp4': return 'video/mp4';
    case 'webm': return 'video/webm';
    case 'ogg': return 'video/ogg';
    case 'mov': return 'video/quicktime';
    case 'avi': return 'video/x-msvideo';
    default: return 'video/mp4';
  }
};

const safeHostname = (maybeUrl) => {
  if (!maybeUrl) return '';
  try {
    // Add protocol if missing for URL parsing
    const withProto = /^(https?:)?\/\//i.test(maybeUrl) ? maybeUrl : `https://${maybeUrl}`;
    return new URL(withProto).hostname;
  } catch {
    return '';
  }
};

/* ---------------------------- Sub-components ------------------------------ */
const PreviewItem = ({ item, index, animationType, animationsEnabled, viewType }) => {
  const [isHovered, setIsHovered] = useState(false);

  const mediaHeight = item.size === 'LARGE' ? 200 : item.size === 'SMALL' ? 80 : 120;

  const renderMedia = () => {
    if (!item.media_url) {
      return (
        <MediaContainer sx={{ height: mediaHeight, border: '2px dashed', borderColor: 'divider' }}>
          <Box textAlign="center">
            <ImageIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              No Media
            </Typography>
          </Box>
        </MediaContainer>
      );
    }

    if (isImageUrl(item.media_url)) {
      return (
        <CardMedia
          component="img"
          height={mediaHeight}
          image={item.media_url}
          alt={item.label || 'Item media'}
          sx={{
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }

    if (isVideoUrl(item.media_url)) {
      return (
        <MediaContainer sx={{ height: mediaHeight }}>
          <video
            width="100%"
            height="100%"
            autoPlay
            loop
            muted
            playsInline
            style={{ objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          >
            <source src={item.media_url} type={getMimeTypeFromExt(item.media_url)} />
          </video>
        </MediaContainer>
      );
    }

    return (
      <MediaContainer sx={{ height: mediaHeight }}>
        <Typography variant="body2" color="text.secondary">
          Unsupported media type
        </Typography>
      </MediaContainer>
    );
  };

  const renderContent = () => {
    if (viewType === 'simple') {
      return (
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {item.media_url ? (
              <Avatar src={isImageUrl(item.media_url) ? item.media_url : undefined} sx={{ width: 40, height: 40 }}>
                {!isImageUrl(item.media_url) && <ImageIcon />}
              </Avatar>
            ) : (
              <Avatar sx={{ width: 40, height: 40 }}><ImageIcon /></Avatar>
            )}
            <Box flex={1}>
              <Typography variant="h6" component="div" fontWeight={600}>
                {item.label || 'Untitled Item'}
              </Typography>
              {item.description && (
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              )}
            </Box>
            <Launch color="primary" />
          </Box>
        </CardContent>
      );
    }

    // complex
    const host = safeHostname(item.url);

    return (
      <>
        {renderMedia()}
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" component="div" fontWeight={600} gutterBottom>
            {item.label || 'Untitled Item'}
          </Typography>
          {item.description && (
            <Typography variant="body2" color="text.secondary" paragraph>
              {item.description}
            </Typography>
          )}
          {item.url && host && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Launch fontSize="small" color="primary" />
              <Typography variant="caption" color="primary">
                {host}
              </Typography>
            </Box>
          )}
        </CardContent>
      </>
    );
  };

  return (
    <AnimatedItem
      size={item.size}
      animationType={animationType}
      animationsEnabled={animationsEnabled}
      delay={index * 0.1}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => { if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer'); }}
      role={item.url ? 'button' : 'region'}
      aria-label={item.label || 'Preview item'}
      tabIndex={0}
    >
      {renderContent()}
    </AnimatedItem>
  );
};

const ContactDisplay = ({ contact }) => {
  if (!contact) return null;

  const initial =
    contact.first_name?.[0] ||
    contact.full_name?.[0] ||
    '?';

  return (
    <ContactInfo elevation={0}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Avatar sx={{ width: 48, height: 48 }}>{initial}</Avatar>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={600}>
            {contact.full_name || 'Contact Name'}
          </Typography>
          {contact.title && (
            <Typography variant="body2" color="text.secondary">
              {contact.title}
            </Typography>
          )}
        </Box>
      </Box>

      <Stack spacing={1}>
        {contact.email && (
          <Box display="flex" alignItems="center" gap={1}>
            <Email fontSize="small" color="action" />
            <Typography variant="body2">{contact.email}</Typography>
          </Box>
        )}
        {contact.phone && (
          <Box display="flex" alignItems="center" gap={1}>
            <Phone fontSize="small" color="action" />
            <Typography variant="body2">{contact.phone}</Typography>
          </Box>
        )}
        {contact.company && (
          <Box display="flex" alignItems="center" gap={1}>
            <Business fontSize="small" color="action" />
            <Typography variant="body2">{contact.company}</Typography>
          </Box>
        )}
        {contact.location && (
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2">{contact.location}</Typography>
          </Box>
        )}
      </Stack>
    </ContactInfo>
  );
};

/* ----------------------------- Main Component ----------------------------- */
export const EnhancedBioPagePreview = ({
  title,
  slug,
  description,
  pageType,
  viewType = 'simple',
  items = [],
  isPublic = true,
  qrEnabled = false,
  animationsEnabled = true,
  animationType = 'FADE_IN',
  linkUserPage = false,
  headerMediaUrl,
  linkedContact,
  customtheme = { backgroundColor: '#ffffff', foregroundColor: '#000000' }
}) => {
  const [previewLoaded, setPreviewLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setPreviewLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!previewLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography>Loading preview...</Typography>
      </Box>
    );
  }

  return (
    <BioPageContainer customtheme={customtheme}>
      {/* Header Media Background */}
      {headerMediaUrl && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 300,
            backgroundImage: `url(${headerMediaUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.3,
            zIndex: 0,
          }}
        />
      )}

      {/* Header Section */}
      <HeaderSection>
        <Fade in timeout={600}>
          <Box position="relative" zIndex={1}>
            <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
              {title || slug || 'Your Bio Page'}
            </Typography>

            {description && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                {description}
              </Typography>
            )}

            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
              {pageType === 'PROFILE' && (
                <Chip
                  icon={<Star />}
                  label="Profile Page"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.8)' }}
                />
              )}

              {isPublic && (
                <Chip
                  icon={<Public />}
                  label="Public"
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.8)' }}
                />
              )}

              {animationsEnabled && (
                <Chip
                  icon={<PlayArrow />}
                  label="Animated"
                  color="secondary"
                  size="small"
                  variant="outlined"
                  sx={{ backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.8)' }}
                />
              )}

              {linkUserPage && (
                <Chip
                  icon={<LinkIcon />}
                  label="Page Linked"
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.8)' }}
                />
              )}
            </Stack>

            {qrEnabled && (
              <QRCodePlaceholder aria-label="QR Code placeholder">
                <Box textAlign="center">
                  <QrCode sx={{ mb: 0.5 }} />
                  <Typography variant="caption">QR</Typography>
                </Box>
              </QRCodePlaceholder>
            )}
          </Box>
        </Fade>
      </HeaderSection>

      {/* Content Section */}
      <ContentSection>
        {/* Linked Contact Info */}
        {linkedContact && (
          <Slide direction="up" in timeout={800}>
            <Box>
              <ContactDisplay contact={linkedContact} />
            </Box>
          </Slide>
        )}

        {/* Page Items */}
        {viewType === 'complex' && items.length > 0 ? (
          <Grid container spacing={2}>
            {items.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={item.size === 'LARGE' ? 12 : 6}
                md={item.size === 'SMALL' ? 4 : item.size === 'LARGE' ? 12 : 6}
                key={`${item.id || item.label || 'item'}-${index}`}
              >
                <PreviewItem
                  item={item}
                  index={index}
                  animationType={animationType}
                  animationsEnabled={animationsEnabled}
                  viewType={viewType}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {items.map((item, index) => (
              <PreviewItem
                key={`${item.id || item.label || 'item'}-${index}`}
                item={item}
                index={index}
                animationType={animationType}
                animationsEnabled={animationsEnabled}
                viewType={viewType}
              />
            ))}
          </Box>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <Fade in timeout={1000}>
            <Box
              textAlign="center"
              py={8}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.5)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <LinkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No items yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Add items to see them appear here
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Profile page link */}
        {linkUserPage && pageType === 'PROFILE' && (
          <Zoom in timeout={1200}>
            <Box textAlign="center" mt={4}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Launch />}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  backdropFilter: 'blur(10px)',
                  bgcolor: 'rgba(255,255,255,0.8)',
                }}
                onClick={() => {
                  // no-op: hook up to route if available
                }}
              >
                View Full Profile
              </Button>
            </Box>
          </Zoom>
        )}
      </ContentSection>
    </BioPageContainer>
  );
};

/* ------------------- Optional simple container (renamed) ------------------- */
// (kept for backward-compatibility without clashing with the main export name)
export const SimpleBioContainer = styled(
  Box,
  { shouldForwardProp: shouldForward(['customtheme']) }
)(({ theme, customtheme }) => ({
  backgroundColor: customtheme?.backgroundColor || theme.palette.background.default,
  color: customtheme?.foregroundColor || theme.palette.text.primary,
  minHeight: '400px',
  width: '100%',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

export default EnhancedBioPagePreview;
